import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import type { SortDescriptor } from "react-aria-components";
import type {
  TargetCollaborationGroupedItem,
  TargetCollaborationListItem,
  TargetCollaborationMetric,
} from "@/api/collaborations";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Table, TableCard } from "@/components/application/table/table";
import { UnboxTableEmptyStateContent } from "@/components/application/table/unbox-table";
import { SEEN_ROW_CLASS, useSeenRows } from "@/components/application/table/use-seen-rows";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";
import { TARGET_COLLABORATION_LIST_EMPTY_STATE } from "./collaboration-empty-states";
import { ExpiryStatusCell } from "./expiry-status-cell";
import { GmvCell } from "./gmv-cell";
import type { GroupExpansionState } from "./group-expansion-controller";
import { ModifiedCell } from "./modified-cell";
import { OverdueCell } from "./overdue-cell";
import { ProductChips } from "./product-chips";
import { SmartUnboxGroupHeader } from "./smart-unbox-group-header";

type Props = {
  data?: TargetCollaborationGroupedItem[];
  /**
   * Lazily-loaded per-row metric counts keyed by the same row key the table
   * uses (`group-${groupId}` for group headers, `collaborationId` otherwise).
   * Cells render a skeleton until the value lands. See UNB-1371.
   */
  metrics?: Record<string, TargetCollaborationMetric>;
  isLoading: boolean;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
  onReset: () => void;
  expandedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
  /**
   * Per-group infinite-query state owned by `GroupExpansionController` in the
   * explorer. The table renders the loaded child rows plus a sentinel/footer
   * row that triggers `fetchNextPage` via IntersectionObserver. (UNB-1371)
   */
  groupExpansions: Map<string, GroupExpansionState>;
  hasFilters?: boolean;
};

const numberFormatter = new Intl.NumberFormat("en-US");

// Shared with the other target-collaboration lists, so a collab opened from one shows as seen in all.
export const TARGET_COLLABORATIONS_TABLE_KEY = "target-collaborations";
export const seenGroupId = (groupId: string) => `group:${groupId}`;

export const MetricSkeleton = () => (
  <span aria-hidden="true" className="inline-block h-3 w-8 rounded bg-quaternary/30 animate-pulse" />
);

/**
 * Resolve a metric value for a row, preferring the value that may already be
 * inlined on the list response (when sorting by that metric the server returns
 * it directly). Falls back to the lazy metrics map. Returns `null` while we're
 * still waiting for the metrics request to land.
 */
export const resolveMetric = (
  inline: number | null,
  metric: TargetCollaborationMetric | undefined,
  field: keyof Omit<TargetCollaborationMetric, "key">,
): number | null => {
  if (inline !== null) return inline;
  if (metric) return metric[field] as number;
  return null;
};

type TopProduct = { title: string; commissionRate: number | null; imageUrl: string | null };

// Renders the name cell with optional expand icon and Smart Unbox badge
const NameCell = ({
  item,
  isGroupHeader,
  isExpanded,
  isChildRow,
  isLastChild,
  groupSize,
  topProducts,
  groupTopProducts,
  onToggle,
}: {
  item: { name: string; groupId?: string | null };
  isGroupHeader: boolean;
  isExpanded: boolean;
  isChildRow: boolean;
  isLastChild: boolean;
  groupSize?: number;
  topProducts?: TopProduct[];
  groupTopProducts?: TopProduct[];
  onToggle?: () => void;
}) => {
  if (isGroupHeader) {
    return (
      <SmartUnboxGroupHeader
        name={item.name}
        isExpanded={isExpanded}
        onToggle={onToggle || (() => {})}
        groupSize={groupSize}
        topProducts={groupTopProducts ?? []}
      />
    );
  }

  return (
    <div className="flex items-start gap-2 h-full max-w-[60ch]">
      {isChildRow && <div className="flex-shrink-0 w-5" />}

      {/* Name text */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-sm font-medium text-primary whitespace-nowrap max-w-[80ch] truncate" title={item.name}>
          {item.name}
        </span>
        {topProducts && topProducts.length > 0 && <ProductChips products={topProducts} />}
      </div>
    </div>
  );
};

type RowKind = "data" | "loading" | "sentinel";

type RenderRow = {
  id: string;
  metricKey: string;
  kind: RowKind;
  item: TargetCollaborationGroupedItem | TargetCollaborationListItem;
  isGroupHeader: boolean;
  isExpanded: boolean;
  isChildRow: boolean;
  isLastChild: boolean;
  isLastInGroup: boolean;
  parentGroupId?: string;
  /** Sentinel rows only — drives infinite scroll for the parent group. */
  sentinel?: {
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
  };
};

/**
 * Sentinel + loading row that drives infinite scroll for an expanded Smart
 * Unbox group. Calls `fetchNextPage` when its 1×1 div intersects the viewport
 * (with 200px of lead-in so the next page is in flight before the user
 * actually reaches the bottom). While `isFetchingNextPage` is true we render
 * a small "Loading more…" indicator so the user gets feedback that the next
 * page is on the way; otherwise the row collapses to just the invisible
 * observer target. See UNB-1371.
 */
export const GroupExpansionSentinel = ({
  sentinel,
}: {
  sentinel: { isFetchingNextPage: boolean; fetchNextPage: () => void };
}) => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !sentinel.isFetchingNextPage) {
            sentinel.fetchNextPage();
          }
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [sentinel.isFetchingNextPage, sentinel.fetchNextPage]);

  return (
    <div className="px-5 pl-10">
      {/* The 1×1 div stays mounted even while fetching so the observer can
          re-fire as soon as the new page arrives if the user is still
          scrolled near the bottom of the group. */}
      <div ref={sentinelRef} aria-hidden="true" className="h-px w-px" />
      {sentinel.isFetchingNextPage && (
        <div className="flex items-center gap-2 py-2 text-tertiary">
          <LoadingIndicator size="sm" />
          <span className="text-xs">Loading more…</span>
        </div>
      )}
    </div>
  );
};

export const TargetCollaborationsTable = ({
  data,
  metrics,
  isLoading,
  sortDescriptor,
  onSortChange,
  onReset,
  expandedGroups,
  onToggleGroup,
  groupExpansions,
  hasFilters,
}: Props) => {
  const navigate = useNavigate();
  const merchant = useMerchant();
  const { isSeen, markSeen, seenCount } = useSeenRows(TARGET_COLLABORATIONS_TABLE_KEY);

  const handleRowClick = (collaborationId: string) => {
    markSeen(collaborationId);
    navigate({
      to: "/merchant/$merchantId/collaborations/target/$collaborationId",
      params: { merchantId: merchant, collaborationId },
    });
  };

  // Flatten top-level rows + per-group expansion (loaded children + sentinel
  // footer) into a single ordered list for react-aria-components. We expose
  // `id` (its row identifier) and `metricKey` (the lookup into the lazy
  // metrics map). Avoid a `key` field on the item — react-aria-components
  // Collection treats `key` as the row identifier and the library's internal
  // cache (CollectionBuilder WeakMap) makes it confusing.
  const renderItems = useMemo(() => {
    if (!data) return [];

    const items: RenderRow[] = [];

    for (const item of data) {
      const isGroupHeader = item.isGroupHeader && item.groupSize > 1;
      const isExpanded = item.groupId ? expandedGroups.has(item.groupId) : false;
      const rowId = item.groupId && isGroupHeader ? `group-${item.groupId}` : item.collaborationId;

      items.push({
        id: rowId,
        metricKey: rowId,
        kind: "data",
        item,
        isGroupHeader,
        isExpanded,
        isChildRow: false,
        isLastChild: false,
        isLastInGroup: false,
      });

      if (!(isGroupHeader && isExpanded && item.groupId)) continue;

      const expansion = groupExpansions.get(item.groupId);
      const members = expansion?.items ?? [];
      const isInitialLoad = !!expansion?.isInitialLoading && members.length === 0;

      if (isInitialLoad) {
        items.push({
          id: `loading-init-${item.groupId}`,
          metricKey: `__loading__:${item.groupId}`,
          kind: "loading",
          item: {
            collaborationId: `loading-init-${item.groupId}`,
            name: "Loading…",
            status: "",
            endTime: null,
            updatedAt: new Date().toISOString(),
            expiryStatus: "on_going",
            totalGmv: null,
            totalGmvCurrency: null,
            topProducts: [],
            invitePendingCount: null,
            sampleRequestedCount: null,
            postOverdueCount: null,
            postedVideosCount: null,
            groupId: null,
            isGroupHeader: false,
            groupSize: 0,
            groupCollaborationIds: [],
            groupUpdatedAtMultiple: false,
            groupTopProducts: [],
          },
          isGroupHeader: false,
          isExpanded: false,
          isChildRow: true,
          isLastChild: true,
          isLastInGroup: true,
          parentGroupId: item.groupId,
        });
      }

      // Only emit a sentinel row when there are actually more pages to load.
      // Once the group is fully loaded, no extra row is rendered at the end.
      const hasSentinel = !!expansion && members.length > 0 && expansion.hasNextPage;

      members.forEach((member, index) => {
        const isLast = index === members.length - 1;
        items.push({
          id: `child-${item.groupId}-${member.collaborationId}`,
          // Children participate in the lazy metrics fetch like top-level
          // rows now (the paginated /target/group/:groupId/members endpoint
          // returns null counts), so the key is the collaborationId.
          metricKey: member.collaborationId,
          kind: "data",
          item: member,
          isGroupHeader: false,
          isExpanded: false,
          isChildRow: true,
          isLastChild: isLast && !hasSentinel,
          isLastInGroup: isLast && !hasSentinel,
          parentGroupId: item.groupId as string,
        });
      });

      if (hasSentinel && expansion) {
        items.push({
          id: `sentinel-${item.groupId}`,
          metricKey: `__sentinel__:${item.groupId}`,
          kind: "sentinel",
          // Stub item — the sentinel renderer doesn't read these fields.
          item: {
            collaborationId: `sentinel-${item.groupId}`,
            name: "",
            status: "",
            endTime: null,
            updatedAt: new Date().toISOString(),
            expiryStatus: "on_going",
            totalGmv: null,
            totalGmvCurrency: null,
            topProducts: [],
            invitePendingCount: null,
            sampleRequestedCount: null,
            postOverdueCount: null,
            postedVideosCount: null,
            groupId: null,
            isGroupHeader: false,
            groupSize: 0,
            groupCollaborationIds: [],
            groupUpdatedAtMultiple: false,
            groupTopProducts: [],
          },
          isGroupHeader: false,
          isExpanded: false,
          isChildRow: true,
          isLastChild: true,
          isLastInGroup: true,
          parentGroupId: item.groupId,
          sentinel: {
            isFetchingNextPage: expansion.isFetchingNextPage,
            fetchNextPage: expansion.fetchNextPage,
          },
        });
      }
    }

    return items;
  }, [data, expandedGroups, groupExpansions]);

  return (
    <TableCard.Root className="bg-secondary_alt flex flex-col w-full flex-1" size="sm" rounded={false}>
      <Table
        aria-label="Target Collaborations"
        selectionMode="none"
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        className={cx((!data || data.length === 0) && "h-full")}
      >
        <Table.Header className="sticky top-0 z-10 bg-primary">
          <Table.Head id="name" label="Name" isRowHeader allowsSorting className="min-w-[260px] px-5 text-xs" />
          <Table.Head id="totalGmv" label="Total GMV" allowsSorting className="px-5 min-w-[120px] text-xs" />
          <Table.Head id="updatedAt" label="Modified" allowsSorting className="px-5 min-w-[110px] text-xs" />
          <Table.Head id="endTime" label="Expiry Status" allowsSorting className="px-5 min-w-[140px] text-xs" />
          <Table.Head
            id="invitePendingCount"
            label="Invite pending"
            allowsSorting
            className="px-5 min-w-[120px] text-xs"
          />
          <Table.Head
            id="sampleRequestedCount"
            label="Sample requests"
            allowsSorting
            className="px-5 min-w-[140px] text-xs"
          />
          <Table.Head id="postOverdueCount" label="Post Overdue" allowsSorting className="px-5 min-w-[130px] text-xs" />
          <Table.Head id="postedVideosCount" label="Videos" allowsSorting className="px-5 min-w-[100px] text-xs" />
        </Table.Header>

        <Table.Body
          items={renderItems}
          // react-aria-components caches each rendered row by item reference,
          // so list every closure value the renderer reads here. Without this,
          // lazy-loaded `metrics` would only become visible after some other
          // `renderItems` invalidation (UNB-1371). See CollectionBuilder.mjs.
          dependencies={[metrics, seenCount]}
          className="text-xs bg-secondary_alt"
          renderEmptyState={() => (
            <UnboxTableEmptyStateContent
              rows={isLoading ? undefined : []}
              hasActiveFilters={hasFilters}
              emptyState={TARGET_COLLABORATION_LIST_EMPTY_STATE}
              filteredEmptyState={{ onClearFilters: onReset }}
            />
          )}
        >
          {({ id, metricKey, kind, item, isGroupHeader, isExpanded, isChildRow, isLastChild, sentinel }) => {
            if (kind === "loading") {
              return (
                <Table.Row id={id} className="bg-primary">
                  <Table.Cell className="px-5 pl-10">
                    <div className="flex items-center gap-2 text-tertiary">
                      <LoadingIndicator size="sm" />
                      <span className="text-xs">Loading collaborations...</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                  <Table.Cell className="px-5" />
                </Table.Row>
              );
            }

            if (kind === "sentinel" && sentinel) {
              return (
                <Table.Row id={id} className="bg-gray-50/50">
                  <Table.Cell className="p-0" colSpan={8}>
                    <GroupExpansionSentinel sentinel={sentinel} />
                  </Table.Cell>
                </Table.Row>
              );
            }

            const groupedItem = item as TargetCollaborationGroupedItem;
            const metric = metrics?.[metricKey];
            const invitePendingCount = resolveMetric(item.invitePendingCount, metric, "invitePendingCount");
            const sampleRequestedCount = resolveMetric(item.sampleRequestedCount, metric, "sampleRequestedCount");
            const postOverdueCount = resolveMetric(item.postOverdueCount, metric, "postOverdueCount");
            const postedVideosCount = resolveMetric(item.postedVideosCount, metric, "postedVideosCount");

            return (
              <Table.Row
                id={id}
                className={cx(
                  "bg-primary transition-colors",
                  // Group headers that are not expanded should toggle on click
                  isGroupHeader && !isExpanded && "cursor-pointer hover:bg-secondary",
                  // Child rows and non-group items navigate to detail
                  !isGroupHeader && "cursor-pointer hover:bg-secondary",
                  // Expanded group headers have a subtle highlight
                  isGroupHeader && isExpanded && "bg-gray-50",
                  // Child rows have indented background
                  isChildRow && "bg-gray-50/50",
                )}
                onAction={() => {
                  if (isGroupHeader) {
                    // For group headers, navigate to group detail page
                    if (groupedItem.groupId) {
                      markSeen(seenGroupId(groupedItem.groupId));
                      navigate({
                        to: "/merchant/$merchantId/collaborations/target-group/$groupId",
                        params: { merchantId: merchant, groupId: groupedItem.groupId },
                      });
                    }
                  } else {
                    // For regular items and child rows, navigate to detail
                    handleRowClick(item.collaborationId);
                  }
                }}
              >
                <Table.Cell
                  className={cx(
                    "px-5",
                    isSeen(
                      isGroupHeader && groupedItem.groupId ? seenGroupId(groupedItem.groupId) : item.collaborationId,
                    ) && SEEN_ROW_CLASS,
                  )}
                >
                  <NameCell
                    item={item}
                    isGroupHeader={isGroupHeader}
                    isExpanded={isExpanded}
                    isChildRow={isChildRow}
                    isLastChild={isLastChild}
                    groupSize={groupedItem.groupSize}
                    topProducts={item.topProducts}
                    groupTopProducts={groupedItem.groupTopProducts}
                    onToggle={
                      isGroupHeader && groupedItem.groupId
                        ? () => onToggleGroup(groupedItem.groupId as string)
                        : undefined
                    }
                  />
                </Table.Cell>

                <Table.Cell className="px-5">
                  <GmvCell value={item.totalGmv} currency={item.totalGmvCurrency} />
                </Table.Cell>

                <Table.Cell className="px-5">
                  <ModifiedCell
                    updatedAt={item.updatedAt}
                    multiple={isGroupHeader && groupedItem.groupUpdatedAtMultiple}
                  />
                </Table.Cell>

                <Table.Cell className="px-5">
                  <ExpiryStatusCell status={item.expiryStatus} endTime={item.endTime} />
                </Table.Cell>

                <Table.Cell className="whitespace-nowrap text-xs text-primary font-medium px-5">
                  {invitePendingCount === null ? (
                    <MetricSkeleton />
                  ) : invitePendingCount > 0 ? (
                    numberFormatter.format(invitePendingCount)
                  ) : (
                    "0"
                  )}
                </Table.Cell>

                <Table.Cell className="whitespace-nowrap text-xs text-primary font-medium px-5">
                  {sampleRequestedCount === null ? (
                    <MetricSkeleton />
                  ) : sampleRequestedCount > 0 ? (
                    numberFormatter.format(sampleRequestedCount)
                  ) : (
                    "0"
                  )}
                </Table.Cell>

                <Table.Cell className="px-5">
                  <OverdueCell count={postOverdueCount} />
                </Table.Cell>

                <Table.Cell className="whitespace-nowrap text-xs text-primary font-medium px-5">
                  {postedVideosCount === null ? (
                    <MetricSkeleton />
                  ) : postedVideosCount > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-success-500" />
                      {postedVideosCount}
                    </span>
                  ) : (
                    "0"
                  )}
                </Table.Cell>
              </Table.Row>
            );
          }}
        </Table.Body>
      </Table>
    </TableCard.Root>
  );
};
